// hooks/useProfileReplies.ts — a user's authored (non-deleted) comments + a reply-count helper.
//
// Backs the profile "Replies" tab: returns every non-deleted comment the user
// has authored (across all posts) and a helper to count direct replies to any
// given comment id. Components must NOT import commentStore directly.

import { useShallow } from 'zustand/react/shallow';
import { useCommentStore } from '@/core/stores/commentStore';
import type { Comment } from '@/core/models/comment';

export interface ProfileRepliesHook {
  /** All non-deleted comments authored by userId (across all posts). */
  replies: Comment[];
  /** Number of non-deleted direct replies to a given comment id. */
  replyCountFor: (parentId: string) => number;
}

export function useProfileReplies(userId: string): ProfileRepliesHook {
  // PATTERN: useShallow is required whenever the selector returns a derived array —
  // without it Zustand would re-render on every store write because the filtered
  // array is a new reference each call.
  const replies = useCommentStore(
    useShallow((s) => s.comments.filter((c) => c.authorId === userId && !c.deleted)),
  );
  const all = useCommentStore(useShallow((s) => s.comments));

  return {
    replies,
    replyCountFor: (parentId) =>
      all.filter((c) => c.parentId === parentId && !c.deleted).length,
  };
}
